<template>
  <div class="pedido-dropdown">
    <button
      class="pedido-dropdown__header"
      @click="toggle"
    >
      <span class="pedido-dropdown__titulo">
        Pedido #{{ pedido.id || pedido.numero }}
      </span>
      <span class="pedido-dropdown__right">
        <b-badge pill :variant="statusVariant" class="pedido-dropdown__badge">
          {{ pedido.status || pedido.situação }}
        </b-badge>
        <i
          class="feather icon-chevron-down pedido-dropdown__chevron"
          :class="{ 'pedido-dropdown__chevron--open': visivel }"
        />
      </span>
    </button>

    <b-collapse v-model="visivel" class="pedido-dropdown__body">
      <div class="pedido-dropdown__scroll-wrapper">
        <div
          ref="scrollContent"
          class="pedido-dropdown__content"
          @scroll="verificarScroll"
        >
          <div v-if="carregando" class="pedido-dropdown__loading">
            <b-spinner small variant="secondary" />
          </div>

          <template v-else-if="detalhes">
            <div v-if="detalhes.cliente && detalhes.cliente.nome" class="info-row">
              <label class="info-row__label">Cliente</label>
              <span class="info-row__value">{{ detalhes.cliente.nome }}</span>
            </div>

            <div v-if="detalhes.cliente && detalhes.cliente.cpfCnpj" class="info-row">
              <label class="info-row__label">CPF/CNPJ</label>
              <span class="info-row__value">{{ detalhes.cliente.cpfCnpj }}</span>
            </div>

            <div v-if="enderecoCompleto" class="info-row">
              <label class="info-row__label">Endereço</label>
              <span class="info-row__value">{{ enderecoCompleto }}</span>
            </div>

            <div v-if="detalhes.data" class="info-row">
              <label class="info-row__label">Data do pedido</label>
              <span class="info-row__value">{{ formatarData(detalhes.data) }}</span>
            </div>

            <div v-if="detalhes.dataEntrega" class="info-row">
              <label class="info-row__label">Previsão de entrega</label>
              <span class="info-row__value">{{ formatarData(detalhes.dataEntrega) }}</span>
            </div>

            <div v-if="detalhes.valorPedido" class="info-row">
              <label class="info-row__label">Valor do pedido</label>
              <span class="info-row__value">{{ detalhes.valorPedido }}</span>
            </div>

            <div v-if="detalhes.valorEntrega" class="info-row">
              <label class="info-row__label">Valor da entrega</label>
              <span class="info-row__value">{{ detalhes.valorEntrega }}</span>
            </div>

            <div v-if="detalhes.valorTotal" class="info-row">
              <label class="info-row__label">Valor total</label>
              <span class="info-row__value info-row__value--destaque">{{ detalhes.valorTotal }}</span>
            </div>

            <div v-if="detalhes.transportadora" class="info-row">
              <label class="info-row__label">Transportadora</label>
              <span class="info-row__value">{{ detalhes.transportadora }}</span>
            </div>

            <div v-if="detalhes.linkRastreio" class="info-row">
              <label class="info-row__label">Rastreio</label>
              <div class="rastreio-actions">
                <a
                  :href="detalhes.linkRastreio"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="rastreio-btn"
                >
                  <i class="feather icon-external-link" />
                  Acompanhar
                </a>
                <button
                  class="rastreio-btn rastreio-btn--outline"
                  @click="copiarLink(detalhes.linkRastreio)"
                >
                  <i class="feather icon-copy" />
                  {{ linkCopiado ? 'Copiado!' : 'Copiar' }}
                </button>
              </div>
            </div>

            <div v-if="detalhes.atrasado" class="info-row">
              <label class="info-row__label">Status da entrega</label>
              <span class="info-row__value info-row__value--alerta">
                ⚠️ Atrasado {{ detalhes.diasAtraso }} dia(s)
              </span>
            </div>

            <!-- Produtos -->
            <div v-if="detalhes.produtos && detalhes.produtos.length" class="info-row">
              <label class="info-row__label">Produtos</label>
              <div
                v-for="(prod, idx) in detalhes.produtos"
                :key="idx"
                class="produto-item"
              >
                <span class="produto-item__nome">{{ prod.nome }}</span>
                <span class="produto-item__meta">
                  Qtd: {{ prod.quantidade }} · {{ prod.subtotal }}
                </span>
              </div>
            </div>

            <!-- Timeline de transporte -->
            <PedidoTimeline
              v-if="detalhes.transportadora"
              origem="Loja"
              :transportadora="detalhes.transportadora"
              :destino="cidadeDestinoFormatada"
            />
          </template>
        </div>

        <!-- Indicador de scroll -->
        <div
          v-if="temMaisScroll"
          class="pedido-dropdown__scroll-hint"
          @click="scrollParaBaixo"
        >
          <i class="feather icon-chevron-down" />
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { buscaPedido } from '@/functions/basics/chat'
import PedidoTimeline from './PedidoTimeline.vue'

export default {
  name: 'PedidoDropdownItem',
  components: {
    PedidoTimeline,
  },
  props: {
    pedido: {
      type: Object,
      required: true,
    },
    clienteId: {
      type: [String, Number],
      required: true,
    },
    fetchPedidoDetalhe: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      visivel: false,
      carregando: false,
      detalhes: null,
      temMaisScroll: false,
      linkCopiado: false,
    }
  },
  computed: {
    statusVariant() {
      const status = (this.pedido.status || this.pedido.situação || '').toLowerCase()
      if (status === 'aprovado') return 'success'
      if (status === 'em transporte' || status === 'transporte') return 'info'
      if (status === 'entregue') return 'primary'
      if (status === 'cancelado') return 'danger'
      return 'secondary'
    },
    enderecoCompleto() {
      if (!this.detalhes) return null
      const partes = [
        this.detalhes.endereco,
        this.detalhes.numeroEndereco,
        this.detalhes.bairro,
        this.detalhes.cidade,
        this.detalhes.estado,
      ].filter(Boolean)
      return partes.length ? partes.join(', ') : null
    },
    cidadeDestinoFormatada() {
      if (!this.detalhes) return ''
      return [this.detalhes.cidade, this.detalhes.estado].filter(Boolean).join('/')
    },
  },
  watch: {
    detalhes() {
      this.$nextTick(() => this.verificarScroll())
    },
    visivel(aberto) {
      if (aberto) {
        this.$nextTick(() => this.verificarScroll())
      } else {
        this.temMaisScroll = false
      }
    },
  },
  methods: {
    async toggle() {
      this.visivel = !this.visivel

      if (this.visivel && !this.detalhes && !this.carregando) {
        this.carregando = true
        try {
          const fetcher = this.fetchPedidoDetalhe || buscaPedido
          this.detalhes = await fetcher(this.clienteId, this.pedido.id || this.pedido.numero)
        } catch (err) {
          console.error('[PedidoDropdownItem] Erro ao buscar pedido:', err)
        } finally {
          this.carregando = false
        }
      }
    },
    verificarScroll() {
      const el = this.$refs.scrollContent
      if (!el) {
        this.temMaisScroll = false
        return
      }
      const margem = 8
      this.temMaisScroll = el.scrollHeight - el.scrollTop - el.clientHeight > margem
    },
    formatarData(valor) {
      if (!valor) return ''
      const d = new Date(valor)
      if (isNaN(d.getTime())) return valor
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    copiarLink(link) {
      navigator.clipboard.writeText(link).then(() => {
        this.linkCopiado = true
        setTimeout(() => { this.linkCopiado = false }, 2000)
      })
    },
    scrollParaBaixo() {
      const el = this.$refs.scrollContent
      if (el) {
        el.scrollBy({ top: 120, behavior: 'smooth' })
      }
    },
  },
}
</script>

<style scoped>
.pedido-dropdown {
  border-bottom: 1px solid #eee;
}

.pedido-dropdown:last-child {
  border-bottom: none;
}

.pedido-dropdown__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: #333;
  font-size: 13px;
  font-weight: 600;
  transition: background-color 0.15s;
}

.pedido-dropdown__header:hover {
  background-color: #f8f5fa;
}

.pedido-dropdown__titulo {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.pedido-dropdown__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.pedido-dropdown__badge {
  font-size: 10px;
}

.pedido-dropdown__chevron {
  font-size: 13px;
  color: #999;
  transition: transform 300ms ease;
}

.pedido-dropdown__chevron--open {
  transform: rotate(180deg);
}

.pedido-dropdown__body {
  transition: height 300ms ease;
}

/* ========== SCROLL WRAPPER ========== */
.pedido-dropdown__scroll-wrapper {
  display: flex;
  flex-direction: column;
}

.pedido-dropdown__content {
  padding: 10px 14px 14px;
  max-height: 280px;
  overflow-y: auto;
}

.pedido-dropdown__content::-webkit-scrollbar {
  width: 3px;
}

.pedido-dropdown__content::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.pedido-dropdown__scroll-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  border-top: 1px solid #eee;
  cursor: pointer;
  color: #9A3092;
  font-size: 14px;
  transition: background-color 0.15s;
  flex-shrink: 0;
}

.pedido-dropdown__scroll-hint:hover {
  background-color: #f8f5fa;
}

.pedido-dropdown__scroll-hint i {
  animation: bounceDown 1.5s ease infinite;
}

@keyframes bounceDown {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
}

.pedido-dropdown__loading {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

/* ========== INFO ROWS ========== */
.info-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

.info-row__label {
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1px;
}

.info-row__value {
  font-size: 13px;
  color: #333;
  word-break: break-word;
}

.info-row__value--destaque {
  font-weight: 600;
  color: #9A3092;
}

.info-row__value--alerta {
  color: #dc3545;
  font-weight: 600;
  font-size: 12px;
}

.produto-item {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  border-bottom: 1px solid #f5f5f5;
}

.produto-item:last-child {
  border-bottom: none;
}

.produto-item__nome {
  font-size: 12px;
  color: #333;
  line-height: 1.3;
  word-break: break-word;
}

.produto-item__meta {
  font-size: 11px;
  color: #999;
}

.info-row__link {
  font-size: 13px;
  color: #9A3092;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.info-row__link:hover {
  text-decoration: underline;
}

.info-row__link i {
  font-size: 11px;
}

/* ========== RASTREIO BUTTONS ========== */
.rastreio-actions {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.rastreio-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 20px;
  border: none;
  background-color: #9A3092;
  color: #fff;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.15s, opacity 0.15s;
  white-space: nowrap;
}

.rastreio-btn:hover {
  background-color: #832980;
  color: #fff;
  text-decoration: none;
}

.rastreio-btn i {
  font-size: 12px;
}

.rastreio-btn--outline {
  background-color: transparent;
  color: #9A3092;
  border: 1px solid #9A3092;
}

.rastreio-btn--outline:hover {
  background-color: #f8f5fa;
  color: #9A3092;
}

/* ========== DARK MODE ========== */
body.dark-layout .pedido-dropdown {
  border-bottom-color: #3b4253;
}

body.dark-layout .pedido-dropdown__header {
  color: #d0d2d6;
}

body.dark-layout .pedido-dropdown__header:hover {
  background-color: #323a52;
}

body.dark-layout .info-row__value {
  color: #d0d2d6;
}

body.dark-layout .info-row__value--destaque {
  color: #c97ec2;
}

body.dark-layout .info-row__link {
  color: #c97ec2;
}

body.dark-layout .produto-item {
  border-bottom-color: #3b4253;
}

body.dark-layout .produto-item__nome {
  color: #d0d2d6;
}

body.dark-layout .rastreio-btn--outline {
  color: #c97ec2;
  border-color: #c97ec2;
}

body.dark-layout .rastreio-btn--outline:hover {
  background-color: #323a52;
}

body.dark-layout .pedido-dropdown__scroll-hint {
  border-top-color: #3b4253;
  color: #c97ec2;
}

body.dark-layout .pedido-dropdown__scroll-hint:hover {
  background-color: #323a52;
}
</style>
